import { useEffect } from "react";
import { IntlShape } from "react-intl";

import defineMessages from "./defineMessages";
import injectIntl from "./injectIntl";
import { getAvailableLogoLocale } from "../parameters";
import { TApp } from "../types";
import { getAppPrefix } from "../app";

const messages = defineMessages({
    description: {
        id: "seo.description",
    },
});

const defaultImage = (lang: string, app: TApp | undefined) =>
    `https://musicators.com/images${getAppPrefix(app)}/og-image-${getAvailableLogoLocale(lang, app)}.jpg`;

interface IProps {
    intl: IntlShape | null;
    title: string;
    description?: string;
    ogTitle?: string;
    ogDescription?: string;
    type?: string;
    image?: string;
    noindex?: boolean;
    nofollow?: boolean;
    lang?: string;
    allMessage?: { [key: string]: string },
    app?: TApp;
}

/** The head a route asks for, as a value. A route's renderSeo returns one of these. */
export type TSeoProps = Omit<IProps, "intl">;

interface ITag {
    name?: string;
    property?: string;
    content: string;
}

/** The title and the tags a page wants in the head, worked out from what it asked for. */
const headFor = ({
    intl, title, description, ogTitle, ogDescription, type, image, noindex, nofollow,
    lang = "en", allMessage, app,
}: IProps): { title: string, meta: ITag[] } => {
    const formatMessage = intl
        ? intl.formatMessage
        : ({ id }: { id: string }) => allMessage![id];
    const fallback = description || formatMessage(messages.description);
    return {
        title,
        meta: [
            { property: "og:title", content: ogTitle || title },
            { name: "description", content: description || formatMessage(messages.description) },
            { property: "og:description", content: ogDescription || fallback },
            { property: "og:type", content: type || "website" },
            { property: "og:image", content: image || defaultImage(intl ? intl.locale : lang, app) },
            { property: "robots", content: (noindex ? "noindex" : "index") + ", " + (nofollow ? "nofollow" : "follow") },
        ],
    };
};

const attribute = (tag: ITag) => (tag.name ? `name="${tag.name}"` : `property="${tag.property}"`);

const escapeHtml = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** A tag whose content never arrived - a translation that is not in the table yet, say. */
const hasContent = (tag: ITag) => typeof tag.content === "string" && tag.content.length > 0;

/**
 * Identity, for the sake of the type check at the call site: a route that misspells a
 * property hears about it where it wrote it rather than further down.
 */
export const seo = (props: TSeoProps) => props;

/**
 * The head as the browser needs it.
 *
 * A route works its own strings out from the message table, which it is handed and hands
 * on in allMessage. That is an input to the work, not part of the answer, and the browser
 * fetches the table for itself anyway - serialising it into the page sends the whole
 * translation file a second time, which on the home page is a quarter of the document.
 */
export const seoForBrowser = ({ allMessage, ...head }: TSeoProps): TSeoProps => head;

/**
 * The head as the strings the document needs.
 *
 * Written out rather than rendered: the server builds its html before it renders the
 * app, and React 19 hoists a rendered title or meta to the front of the render - which
 * here would be inside the container the browser hydrates.
 */
export const renderSeoHead = (props: TSeoProps) => {
    const { title, meta } = headFor({ ...props, intl: null });
    return {
        title: `<title>${escapeHtml(title || "")}</title>`,
        meta: meta.filter(hasContent).map(tag => `<meta ${attribute(tag)} content="${escapeHtml(tag.content)}"/>`).join(""),
    };
};

/**
 * Keeps the document's head in step with the page.
 *
 * Renders nothing: the head the server sent is already right, and this is what keeps it
 * right after a navigation, which the server's copy cannot do. Written against the
 * document rather than rendered into the tree, so that the markup the browser hydrates
 * is the markup the server sent.
 */
const Seo = (props: IProps) => {
    const { title, meta } = headFor(props);
    useEffect(() => {
        if (title) document.title = title;
        for (const tag of meta.filter(hasContent)) {
            const selector = tag.name ? `meta[name="${tag.name}"]` : `meta[property="${tag.property}"]`;
            let element = document.head.querySelector(selector);
            if (!element) {
                element = document.createElement("meta");
                element.setAttribute(tag.name ? "name" : "property", (tag.name || tag.property)!);
                document.head.appendChild(element);
            }
            element.setAttribute("content", tag.content);
        }
    });
    return null;
};

export default injectIntl(Seo);
